import { apiSlice } from 'store/api/apiSlice';
import { peelYamiData } from 'utils/yamiEnvelope';
import { parseUrlQuery } from 'utils/parsers';

/**
 * Cashier financial endpoints require an Idempotency-Key header
 * (main-service `beginCashierFinancialIdempotency` rejects requests without one
 * as MISSING_KEY → "This action could not be completed."). One fresh key per
 * submit.
 */
function createIdempotencyKey(prefix: string) {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return `${prefix}:${cryptoApi.randomUUID()}`;
  }
  return `${prefix}:${Date.now()}:${Math.random().toString(36).slice(2, 12)}`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function unwrapCashierResponse<T>(res: unknown): T {
  const inner = peelYamiData(res);
  if (isPlainObject(res) && res.ok === true && isPlainObject(res.data)) {
    return { ...inner, success: inner.success !== false } as T;
  }
  return inner as T;
}

/** Exactly one of phone or customerUserId must be set. */
export type CashierWalletDepositBody = {
  amount: number;
  note?: string;
  phone?: string;
  customerUserId?: string;
};

export type CashierWalletDepositResponse = {
  success: boolean;
  message?: string;
  amount?: number;
  customer?: {
    id: string;
    phone?: string;
    previousBalance?: number;
    newBalance: number;
  };
  cashierTillBalance?: number;
};

export type CashierWalletWithdrawBody = CashierWalletDepositBody;
export type CashierWalletWithdrawResponse = CashierWalletDepositResponse;

export type CashierTransactionAction =
  | 'deposit'
  | 'withdrawal'
  | 'print'
  | 'till_deposit'
  | 'till_debit'
  | 'adjustment'
  | 'place_bet'
  | 'redeem'
  | 'cancel';

export type CashierTransactionRow = {
  id: string;
  action: CashierTransactionAction;
  actionLabel: string;
  amount: number;
  balanceAfter: number | null;
  note: string;
  sourceType: string;
  timestamp: string;
  referenceId?: string;
  referenceType?: string;
  betLookupId?: string;
  betId?: string;
  sortId?: string;
  customer: {
    id: string;
    phone?: string;
    username?: string;
  } | null;
  by?: {
    id?: string | null;
    username?: string;
  } | null;
};

export type CashierCodePreview = {
  code: string;
  kind: 'deposit' | 'withdraw';
  amount: number;
  currency: string;
  expiresAt: string;
  customer: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
  };
  redeemEndpoint: string;
  payoutBlocked: boolean;
  payoutBlockedReason: string | null;
};

export type CashierWalletWithdrawByCodeBody = {
  code: string;
};

export type CashierWalletWithdrawByCodeResponse = {
  success: boolean;
  message?: string;
  amount?: number;
  customer?: {
    id: string;
    phone?: string;
    previousBalance?: number;
    newBalance: number;
  };
  cashierTillBalance?: number;
};

export type CashierReceiptOptions = {
  enabled: boolean;
  banks: string[];
  tillDebited?: boolean;
  note?: string;
  companyAccounts?: Array<{
    bank: string;
    label: string;
    account: string;
    holder: string;
  }>;
};

export type WalletRequestRow = {
  id: string;
  requestCode?: string | null;
  customer_name: string;
  customer_phone: string;
  request_type: string;
  amount: number;
  status: string;
  currency: string;
  created_at: string;
  destinationNote?: string;
  payoutAccount?: string;
  preferredMethod?: string;
  completedMethod?: string | null;
  performedByUsername?: string | null;
  receiptReference?: string | null;
  completedBank?: string | null;
};

export type WalletRequestLookup = {
  kind: 'deposit' | 'withdraw';
  request: WalletRequestRow;
  payoutAccount?: string;
  companyAccounts?: CashierReceiptOptions['companyAccounts'];
  receiptDepositEnabled?: boolean;
  receiptPayoutEnabled?: boolean;
};

export type CashierReceiptVerifyBody = {
  bank: string;
  receipt?: string;
  ft?: string;
  account?: string;
  amount?: number | string;
};

export type CashierReceiptVerifyResponse = {
  receipt?: Record<string, unknown>;
  creditAmount?: number;
  reference?: string;
  alreadyUsed?: boolean;
  usedByUserId?: string | null;
};

export type CashierReceiptDepositBody = CashierReceiptVerifyBody & {
  note?: string;
  phone?: string;
  customerUserId?: string;
};

export const cashierRetailApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    cashierWalletDeposit: builder.mutation<CashierWalletDepositResponse, CashierWalletDepositBody>({
      query: (body) => ({
        url: '/api/cashier/wallet-deposit',
        method: 'POST',
        body,
        headers: {
          'Idempotency-Key': createIdempotencyKey('cashier-wallet-deposit'),
        },
      }),
      transformResponse(res: unknown) {
        return unwrapCashierResponse<CashierWalletDepositResponse>(res);
      },
      invalidatesTags: ['CashierSummary', 'CashierProfile'],
    }),
    cashierReceiptOptions: builder.query<CashierReceiptOptions, void>({
      query: () => ({ url: '/api/cashier/receipt/options' }),
      transformResponse(res: unknown) {
        return peelYamiData(res) as CashierReceiptOptions;
      },
      keepUnusedDataFor: 30,
    }),
    cashierReceiptVerify: builder.mutation<CashierReceiptVerifyResponse, CashierReceiptVerifyBody>({
      query: (body) => ({
        url: '/api/cashier/receipt/verify',
        method: 'POST',
        body,
      }),
      transformResponse(res: unknown) {
        return peelYamiData(res) as CashierReceiptVerifyResponse;
      },
    }),
    cashierReceiptDeposit: builder.mutation<
      CashierWalletDepositResponse,
      CashierReceiptDepositBody
    >({
      query: (body) => ({
        url: '/api/cashier/receipt/deposit',
        method: 'POST',
        body,
        headers: {
          'Idempotency-Key': createIdempotencyKey('cashier-receipt-deposit'),
        },
      }),
      transformResponse(res: unknown) {
        return unwrapCashierResponse<CashierWalletDepositResponse>(res);
      },
      invalidatesTags: ['CashierSummary', 'CashierProfile'],
    }),
    createCashierDepositRequest: builder.mutation<
      { success: boolean; message?: string; requestCode?: string },
      { amount: number; phone?: string; customerUserId?: string }
    >({
      query: (body) => ({
        url: '/api/cashier/deposit-requests/create',
        method: 'POST',
        body,
        headers: {
          'Idempotency-Key': createIdempotencyKey('cashier-create-deposit-req'),
        },
      }),
      transformResponse(res: unknown) {
        return peelYamiData(res) as { success: boolean; message?: string; requestCode?: string };
      },
      invalidatesTags: ['WalletRequests'],
    }),
    cashierWalletWithdraw: builder.mutation<CashierWalletWithdrawResponse, CashierWalletWithdrawBody>({
      query: (body) => ({
        url: '/api/cashier/wallet-withdraw',
        method: 'POST',
        body,
        headers: {
          'Idempotency-Key': createIdempotencyKey('cashier-wallet-withdraw'),
        },
      }),
      transformResponse(res: unknown) {
        return unwrapCashierResponse<CashierWalletWithdrawResponse>(res);
      },
      invalidatesTags: ['CashierSummary', 'CashierProfile'],
    }),
    previewCashierCode: builder.query<CashierCodePreview, string>({
      query: (code) => ({
        url: `/api/cashier/wallet-code/${encodeURIComponent(code.trim().toUpperCase())}`,
      }),
      transformResponse(res: unknown) {
        return peelYamiData(res) as CashierCodePreview;
      },
      keepUnusedDataFor: 0,
    }),
    cashierWalletWithdrawByCode: builder.mutation<
      CashierWalletWithdrawByCodeResponse,
      CashierWalletWithdrawByCodeBody
    >({
      query: ({ code }) => ({
        url: '/api/cashier/wallet-withdraw-by-code',
        method: 'POST',
        body: { code: code.trim().toUpperCase() },
        headers: {
          'Idempotency-Key': createIdempotencyKey('cashier-wallet-withdraw-by-code'),
        },
      }),
      transformResponse(res: unknown) {
        return unwrapCashierResponse<CashierWalletWithdrawByCodeResponse>(res);
      },
      invalidatesTags: ['CashierSummary', 'CashierProfile', 'WalletRequests'],
    }),
    getWalletRequests: builder.query<
      {
        requests: Array<{
          id: string;
          customer_name: string;
          customer_phone: string;
          request_type: string;
          amount: number;
          status: string;
          currency: string;
          created_at: string;
          destinationNote?: string;
          preferredMethod?: string;
          completedMethod?: string | null;
          performedByUsername?: string | null;
          receiptReference?: string | null;
          completedBank?: string | null;
        }>;
        total: number;
        receiptPayoutEnabled?: boolean;
        banks?: string[];
      },
      { status?: 'pending' | 'all'; limit?: number }
    >({
      query: ({ status = 'pending', limit = 50 }) => ({
        url: `/api/cashier/withdraw-requests?status=${status}&limit=${limit}`,
      }),
      transformResponse(res: unknown) {
        const peeled = peelYamiData(res) as {
          requests?: Array<Record<string, unknown>>;
          count?: number;
          receiptPayoutEnabled?: boolean;
          banks?: string[];
        };
        const rows = Array.isArray(peeled?.requests) ? peeled.requests : [];
        return {
          requests: rows.map((raw) => {
            const customer = (raw.customer as Record<string, unknown>) || {};
            return {
              id: String(raw.id ?? ''),
              requestCode: raw.requestCode != null ? String(raw.requestCode) : null,
              customer_name: String(
                raw.customer_name ??
                  customer.fullName ??
                  customer.username ??
                  customer.phone ??
                  'Customer',
              ),
              customer_phone: String(raw.customer_phone ?? customer.phone ?? ''),
              request_type: 'withdrawal',
              amount: Number(raw.amount ?? 0),
              status: String(raw.status ?? 'pending'),
              currency: String(raw.currency ?? 'ETB'),
              created_at: String(raw.created_at ?? raw.createdAt ?? ''),
              destinationNote: raw.destinationNote ? String(raw.destinationNote) : '',
              payoutAccount: String(
                raw.payoutAccount ?? raw.destinationNote ?? customer.phone ?? '',
              ),
              preferredMethod: raw.preferredMethod ? String(raw.preferredMethod) : 'any',
              completedMethod:
                raw.completedMethod != null ? String(raw.completedMethod) : null,
              performedByUsername:
                raw.performedByUsername != null ? String(raw.performedByUsername) : null,
              receiptReference:
                raw.receiptReference != null ? String(raw.receiptReference) : null,
              completedBank: raw.completedBank != null ? String(raw.completedBank) : null,
            };
          }),
          total: Number(peeled?.count ?? rows.length),
          receiptPayoutEnabled: Boolean(peeled?.receiptPayoutEnabled),
          banks: Array.isArray(peeled?.banks) ? peeled.banks.map(String) : ['tele', 'cbe'],
        };
      },
      providesTags: ['WalletRequests'],
    }),
    approveWalletRequest: builder.mutation<{ status: string; message: string }, { id: string }>({
      query: ({ id }) => ({
        url: `/api/cashier/withdraw-requests/${encodeURIComponent(id)}/complete-cash`,
        method: 'POST',
        body: {},
        headers: {
          'Idempotency-Key': createIdempotencyKey('cashier-withdraw-request-cash'),
        },
      }),
      transformResponse(res: unknown) {
        const peeled = peelYamiData(res) as { message?: string };
        return {
          status: 'completed',
          message: String(peeled?.message ?? 'Cash withdrawal completed'),
        };
      },
      invalidatesTags: ['WalletRequests', 'CashierSummary', 'CashierProfile'],
    }),
    completeWithdrawByReceipt: builder.mutation<
      { status: string; message: string },
      { id: string; bank: string; receipt: string; ft?: string; account?: string; note?: string }
    >({
      query: ({ id, bank, receipt, ft, account, note }) => ({
        url: `/api/cashier/withdraw-requests/${encodeURIComponent(id)}/complete-by-receipt`,
        method: 'POST',
        body: { bank, receipt, ft, account, note },
        headers: {
          'Idempotency-Key': createIdempotencyKey('cashier-withdraw-request-receipt'),
        },
      }),
      transformResponse(res: unknown) {
        const peeled = peelYamiData(res) as { message?: string };
        return {
          status: 'completed',
          message: String(peeled?.message ?? 'Payout verified'),
        };
      },
      invalidatesTags: ['WalletRequests', 'CashierSummary', 'CashierProfile'],
    }),
    rejectWalletRequest: builder.mutation<{ status: string; message: string }, string>({
      query: (id) => ({
        url: `/api/cashier/withdraw-requests/${encodeURIComponent(id)}/reject`,
        method: 'POST',
        body: {},
      }),
      transformResponse(res: unknown) {
        const peeled = peelYamiData(res) as { message?: string };
        return {
          status: 'rejected',
          message: String(peeled?.message ?? 'Request rejected'),
        };
      },
      invalidatesTags: ['WalletRequests'],
    }),
    lookupWalletRequestByCode: builder.query<WalletRequestLookup, string>({
      query: (code) => ({
        url: `/api/cashier/wallet-request/lookup?code=${encodeURIComponent(code.trim())}`,
      }),
      transformResponse(res: unknown) {
        return peelYamiData(res) as WalletRequestLookup;
      },
    }),
    getDepositRequests: builder.query<
      {
        requests: WalletRequestRow[];
        total: number;
        receiptDepositEnabled?: boolean;
        banks?: string[];
        companyAccounts?: CashierReceiptOptions['companyAccounts'];
      },
      { status?: 'pending' | 'all'; limit?: number }
    >({
      query: ({ status = 'pending', limit = 50 }) => ({
        url: `/api/cashier/deposit-requests?status=${status}&limit=${limit}`,
      }),
      transformResponse(res: unknown) {
        const peeled = peelYamiData(res) as {
          requests?: Array<Record<string, unknown>>;
          count?: number;
          receiptDepositEnabled?: boolean;
          banks?: string[];
          companyAccounts?: CashierReceiptOptions['companyAccounts'];
        };
        const rows = Array.isArray(peeled?.requests) ? peeled.requests : [];
        return {
          requests: rows.map((raw) => {
            const customer = (raw.customer as Record<string, unknown>) || {};
            return {
              id: String(raw.id ?? ''),
              requestCode: raw.requestCode != null ? String(raw.requestCode) : null,
              customer_name: String(
                raw.customer_name ??
                  customer.fullName ??
                  customer.username ??
                  customer.phone ??
                  'Customer',
              ),
              customer_phone: String(raw.customer_phone ?? customer.phone ?? ''),
              request_type: 'deposit',
              amount: Number(raw.amount ?? 0),
              status: String(raw.status ?? 'pending'),
              currency: String(raw.currency ?? 'ETB'),
              created_at: String(raw.created_at ?? raw.createdAt ?? ''),
            };
          }),
          total: Number(peeled?.count ?? rows.length),
          receiptDepositEnabled: Boolean(peeled?.receiptDepositEnabled),
          banks: Array.isArray(peeled?.banks) ? peeled.banks.map(String) : ['tele', 'cbe'],
          companyAccounts: peeled?.companyAccounts,
        };
      },
      providesTags: ['WalletRequests'],
    }),
    completeDepositByReceipt: builder.mutation<
      { status: string; message: string },
      { id: string; bank: string; receipt: string; ft?: string; account?: string; note?: string }
    >({
      query: ({ id, bank, receipt, ft, account, note }) => ({
        url: `/api/cashier/deposit-requests/${encodeURIComponent(id)}/complete-by-receipt`,
        method: 'POST',
        body: { bank, receipt, ft, account, note },
        headers: {
          'Idempotency-Key': createIdempotencyKey('cashier-deposit-request-receipt'),
        },
      }),
      transformResponse(res: unknown) {
        const peeled = peelYamiData(res) as { message?: string };
        return {
          status: 'completed',
          message: String(peeled?.message ?? 'Deposit verified'),
        };
      },
      invalidatesTags: ['WalletRequests', 'CashierSummary', 'CashierProfile'],
    }),
    rejectDepositRequest: builder.mutation<{ status: string; message: string }, string>({
      query: (id) => ({
        url: `/api/cashier/deposit-requests/${encodeURIComponent(id)}/reject`,
        method: 'POST',
        body: {},
      }),
      transformResponse(res: unknown) {
        const peeled = peelYamiData(res) as { message?: string };
        return {
          status: 'rejected',
          message: String(peeled?.message ?? 'Request rejected'),
        };
      },
      invalidatesTags: ['WalletRequests'],
    }),
    getCashierTransactions: builder.query({
      query: (query) => ({
        url: parseUrlQuery('/api/cashier/transactions', query),
      }),
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      transformResponse(res: unknown) {
        const inner = peelYamiData(res) as {
          results?: CashierTransactionRow[];
          page_metadata?: {
            page?: number;
            page_size?: number;
            count?: number;
            total_pages?: number;
          };
        };
        const rows = Array.isArray(inner?.results) ? inner.results : [];
        const meta = inner?.page_metadata || {};
        return {
          results: rows,
          page_metadata: {
            page: Number(meta.page || 1),
            page_size: Number(meta.page_size || 10),
            count: Number(meta.count || 0),
            total_pages: Number(meta.total_pages || 1),
          },
        };
      },
      providesTags: ['CashierSummary'],
    }),
  }),
});

export const {
  useCashierWalletDepositMutation,
  useCashierReceiptOptionsQuery,
  useCashierReceiptVerifyMutation,
  useCashierReceiptDepositMutation,
  useCreateCashierDepositRequestMutation,
  useCashierWalletWithdrawMutation,
  useLazyPreviewCashierCodeQuery,
  useCashierWalletWithdrawByCodeMutation,
  useGetWalletRequestsQuery,
  useApproveWalletRequestMutation,
  useCompleteWithdrawByReceiptMutation,
  useRejectWalletRequestMutation,
  useLazyLookupWalletRequestByCodeQuery,
  useGetDepositRequestsQuery,
  useCompleteDepositByReceiptMutation,
  useRejectDepositRequestMutation,
  useGetCashierTransactionsQuery,
} = cashierRetailApiSlice;
